import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

import KidLayout from '../../components/Layout/KidLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';

import { useKidAuth } from '../../contexts/KidAuthContext';
import { dbKidData as db } from '../../services/supabase';

import { formatCurrency } from '../../utils/currency';
import '../../styles/brand-typography.css';
import './KidChores.css';

const KidChores = () => {
  const navigate = useNavigate();

  const {
    currentKid,
    kidFamily,
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useKidAuth();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;

  const [chores, setChores] = useState([]);
  const [claims, setClaims] = useState([]);

  const [loading, setLoading] = useState(true);

  const [
    submittingChoreId,
    setSubmittingChoreId,
  ] = useState(null);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // --------------------------------------------------
  // Per-child approval setting
  // --------------------------------------------------
  //
  // TRUE:
  // every completed chore requires parent approval.
  //
  // FALSE:
  // every completed chore is automatically approved.
  //
  // Applies equally to:
  //
  // - parent-created chores
  // - starter chores
  // - kid-created personal chores
  // --------------------------------------------------

  const needsChoreReview =
    currentKid?.require_chore_approval !== false;


  // --------------------------------------------------
  // Today's date
  // --------------------------------------------------
  //
  // Match the server-authoritative family-local date used
  // by the chore-claim insert trigger.
  // --------------------------------------------------

  const getTodayDate = () => {
    const parts = new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone:
          kidFamily?.time_zone ||
          'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }
    ).formatToParts(new Date());

    const values = Object.fromEntries(
      parts.map((part) => [part.type, part.value])
    );

    return `${values.year}-${values.month}-${values.day}`;
  };


  // --------------------------------------------------
  // Load chores + claims
  // --------------------------------------------------

  const loadChoresAndClaims = async ({
    showLoading = false,
  } = {}) => {
    if (
      !currentKid?.id ||
      !currentKid?.family_id
    ) {
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }

      setError('');

      const [
        choreData,
        claimData,
      ] = await Promise.all([
        db.getChores(
          currentKid.family_id,
          currentKid.id
        ),

        db.getMyChoreClaims(
          currentKid.family_id,
          currentKid.id
        ),
      ]);

      setChores(
        choreData || []
      );

      setClaims(
        claimData || []
      );
    } catch (err) {
      console.error(
        'KidChores: load error:',
        err
      );

      setError(
        err?.message ||
          'Unable to load chores right now.'
      );
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };


  // --------------------------------------------------
  // Initial load
  // --------------------------------------------------

  useEffect(() => {
    if (
      !currentKid?.id ||
      !currentKid?.family_id
    ) {
      return;
    }

    loadChoresAndClaims({
      showLoading: true,
    });
  }, [
    currentKid?.id,
    currentKid?.family_id,
  ]);


  // --------------------------------------------------
  // Today's claim for each chore
  // --------------------------------------------------
  //
  // IMPORTANT:
  //
  // Historical pending/approved claims do NOT disable
  // the chore.
  //
  // We only care whether this specific chore has been
  // completed TODAY.
  // --------------------------------------------------

  const todayClaimByChoreId =
    useMemo(() => {
      const map = new Map();

      const today =
        getTodayDate();

      (claims || []).forEach(
        (claim) => {
          if (
            !claim?.chore_id ||
            !claim?.completed_on
          ) {
            return;
          }

          if (
            claim.completed_on !==
            today
          ) {
            return;
          }

          const existing =
            map.get(
              claim.chore_id
            );

          if (!existing) {
            map.set(
              claim.chore_id,
              claim
            );

            return;
          }

          const existingTime =
            new Date(
              existing.claimed_at
            ).getTime();

          const claimTime =
            new Date(
              claim.claimed_at
            ).getTime();

          if (
            claimTime >
            existingTime
          ) {
            map.set(
              claim.chore_id,
              claim
            );
          }
        }
      );

      return map;
    }, [claims]);


  // --------------------------------------------------
  // Count pending claims
  // --------------------------------------------------
  //
  // This is informational only.
  //
  // Pending claims from previous days do NOT prevent
  // the kid from completing today's chore.
  // --------------------------------------------------

  const pendingClaimCount =
    useMemo(() => {
      return (
        claims || []
      ).filter(
        (claim) =>
          claim.status ===
          'pending'
      ).length;
    }, [claims]);


  // --------------------------------------------------
  // Refresh claims
  // --------------------------------------------------

  const refreshClaims =
    async () => {
      if (
        !currentKid?.id ||
        !currentKid?.family_id
      ) {
        return;
      }

      try {
        const claimData =
          await db.getMyChoreClaims(
            currentKid.family_id,
            currentKid.id
          );

        setClaims(
          claimData || []
        );
      } catch (err) {
        console.error(
          'KidChores: refresh claims error:',
          err
        );
      }
    };


  // --------------------------------------------------
  // Complete chore
  // --------------------------------------------------

  const completeChore =
    async (chore) => {
      if (isWriteLocked) {
        setError(
          'Your MyPiggy is currently read-only.'
        );
        return;
      }
      if (
        !currentKid?.id ||
        !currentKid?.family_id
      ) {
        setError(
          'Your profile is still loading. Please try again.'
        );

        return;
      }

      // ----------------------------------------------
      // Browser double-click protection
      // ----------------------------------------------

      if (
        submittingChoreId ===
        chore.id
      ) {
        return;
      }

      try {
        setError('');
        setMessage('');

        // Disable the clicked chore immediately.
        setSubmittingChoreId(
          chore.id
        );


        // --------------------------------------------
        // Refresh before submitting
        // --------------------------------------------
        //
        // This improves UX if another tab/device
        // already completed the chore today.
        //
        // PostgreSQL remains the final authority.
        // --------------------------------------------

        const latestClaims =
          await db.getMyChoreClaims(
            currentKid.family_id,
            currentKid.id
          );

        setClaims(
          latestClaims || []
        );


        const today =
          getTodayDate();


        const existingTodayClaim =
          (
            latestClaims || []
          ).find(
            (claim) =>
              claim.chore_id ===
                chore.id &&
              claim.completed_on ===
                today
          );


        if (existingTodayClaim) {
          setMessage(
            `✅ You already completed "${chore.title}" today.`
          );

          return;
        }


        // --------------------------------------------
        // Atomic database operation
        // --------------------------------------------
        //
        // React sends ONLY the chore ID.
        //
        // PostgreSQL determines:
        //
        // - authenticated kid
        // - family
        // - chore reward
        // - chore assignment
        // - today's completion date
        // - whether approval is required
        // - transaction status
        // - claim status
        //
        // Transaction + claim succeed together
        // or roll back together.
        // --------------------------------------------

        const result =
          await db.completeChore(
            chore.id
          );


        // --------------------------------------------
        // Refresh claims
        // --------------------------------------------

        await refreshClaims();


        // --------------------------------------------
        // Kid-friendly feedback
        // --------------------------------------------

        if (
          result?.requires_parent_approval
        ) {
          setMessage(
            `🎉 Great job! You finished "${result.chore_title}". ` +
              `Your parent needs to approve it before ${formatCurrency(
                result.amount_cents
              )} is added to your balance.`
          );
        } else {
          setMessage(
            `🎉 Great job! You earned ${formatCurrency(
              result.amount_cents
            )} for "${result.chore_title}".`
          );
        }
      } catch (err) {
        console.error(
          'KidChores: complete chore error:',
          err
        );

        setError(
          err?.message ||
            'Unable to complete this chore right now.'
        );


        // --------------------------------------------
        // Refresh after error
        // --------------------------------------------
        //
        // If another request succeeded while this one
        // was rejected as a same-day duplicate, the UI
        // will still update correctly.
        // --------------------------------------------

        await refreshClaims();
      } finally {
        setSubmittingChoreId(
          null
        );
      }
    };


  // --------------------------------------------------
  // Missing kid
  // --------------------------------------------------

  if (!currentKid) {
    navigate('/kid/dashboard');

    return null;
  }


  // --------------------------------------------------
  // Loading
  // --------------------------------------------------

  if (loading) {
    return (
      <KidLayout
        className="mypiggy-brand kid-chores"
      title="Do Chores"
        avatar={
          currentKid.avatar
        }
        showBack
      >
        <div className="text-center py-8 text-white">
          Loading chores...
        </div>
      </KidLayout>
    );
  }


  // --------------------------------------------------
  // Page
  // --------------------------------------------------

  return (
    <KidLayout
      className="mypiggy-brand kid-chores"
      title="Do Chores"
      avatar={
        currentKid.avatar
      }
      showBack
    >
      <div className="space-y-4">
        <Card className="p-4">
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-950">
            <MyPiggyIcon name="do-chores" size="sm" />
            My Chores
          </h2>
          <p className="mt-1 text-base font-bold leading-relaxed text-slate-700">
            Finished a chore? Press <span className="whitespace-nowrap text-purple-800 font-extrabold">I Did It!</span> next to it.
          </p>
          <details className={`chore-reward-help mt-3 rounded-lg border px-3 ${needsChoreReview ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-teal-200 bg-teal-50 text-teal-900'}`}>
            <summary className="cursor-pointer py-2 text-sm font-extrabold">
              {needsChoreReview ? 'How rewards work · Parent approval' : 'How rewards work · Automatic'}
            </summary>
            <p className="pb-3 text-base font-semibold leading-relaxed">
              {needsChoreReview
                ? 'Your parent checks completed chores before the reward is added to your Available Money.'
                : 'Your reward is added to your Available Money when you press I Did It! Only press it after you finish the chore.'}
            </p>
          </details>
          {pendingClaimCount > 0 && (
            <p className="mt-2 text-sm font-bold text-amber-900">
              {pendingClaimCount} completed {pendingClaimCount === 1 ? 'chore is' : 'chores are'} waiting for parent approval.
            </p>
          )}
        </Card>

        <Button
          variant="kid"
          className="w-full chore-add-button"
          disabled={isWriteLocked}
          onClick={() => navigate('/kid/chores/new')}
        >
          <MyPiggyIcon name="do-chores" size="sm" className="mr-2" />
          Add My Own Chore
        </Button>

        {/* ========================================
            Information / success
           ======================================== */}

        {message ? (
          <Card className="p-4 border border-green-200 bg-green-50">
            <p role="status" className="text-green-800 text-base font-bold">
              {message}
            </p>
          </Card>
        ) : null}


        {/* ========================================
            Error
           ======================================== */}

        {error ? (
          <Card className="p-4 border border-red-200 bg-red-50">
            <p role="alert" className="text-red-700 text-base font-bold">
              {error}
            </p>
          </Card>
        ) : null}


        {/* ========================================
            No chores
           ======================================== */}

        {chores.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-lg font-bold mb-2">
              No chores yet
            </p>

            <p className="text-gray-600">
              Add your own chore above,
              or ask your parent to add
              chores in the Parent Portal.
            </p>
          </Card>
        ) : (
          // ======================================
          // Chore list
          // ======================================

          <Card className="overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
              <h3 className="font-extrabold text-lg text-slate-950">Your Chores</h3>
              <span className="text-sm font-bold text-slate-600">{chores.length} {chores.length === 1 ? 'chore' : 'chores'}</span>
            </div>
            <ul className="divide-y divide-slate-200">
            {chores.map(
              (chore) => {

                const todayClaim =
                  todayClaimByChoreId.get(
                    chore.id
                  );


                const isPendingToday =
                  todayClaim?.status ===
                  'pending';


                const isApprovedToday =
                  todayClaim?.status ===
                  'approved';


                const isRejectedToday =
                  todayClaim?.status ===
                  'rejected';


                const isDoneToday =
                  Boolean(
                    todayClaim
                  );


                const isSubmitting =
                  submittingChoreId ===
                  chore.id;


                const cannotComplete =
                  isWriteLocked ||
                  isSubmitting ||
                  isDoneToday;


                return (
                  <li
                    key={chore.id}
                    className="px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">

                      {/* Chore details */}

                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold text-base sm:text-lg leading-snug text-slate-950 break-words">
                          {chore.title}
                        </p>


                        <p className="mt-1 text-sm font-bold text-slate-600">
                          Reward:{' '}

                          <span className="font-extrabold text-teal-800 text-base">
                            {formatCurrency(
                              chore.value_cents
                            )}
                          </span>
                        </p>


                        {/* Personal chore */}

                        {chore.created_by ===
                        'child' ? (
                          <p className="flex items-center gap-1 mt-1 text-sm text-purple-800">
                            <MyPiggyIcon name="kid-profile" size="xs" />
                            My personal
                            chore
                          </p>
                        ) : null}


                        {/* Today's status */}

                        {isPendingToday ? (
                          <p className="flex items-center gap-1 mt-1 text-sm text-amber-900 font-semibold">
                            <MyPiggyIcon name="chores-waiting" size="xs" />
                            Done today —
                            waiting for parent
                            approval
                          </p>
                        ) : null}


                        {isApprovedToday ? (
                          <p className="flex items-center gap-1 mt-1 text-sm text-teal-800 font-semibold">
                            <MyPiggyIcon name="chore-approved" size="xs" />
                            Done today
                          </p>
                        ) : null}


                        {isRejectedToday ? (
                          <p className="flex items-center gap-1 mt-1 text-sm text-red-800 font-semibold">
                            <MyPiggyIcon name="chore-needs-changes" size="xs" />
                            Today's chore
                            was not approved
                          </p>
                        ) : null}
                      </div>


                      {/* Complete button */}

                      <div>
                        <Button
                          variant="kid"
                          className="chore-complete-button"
                          aria-label={`${isDoneToday ? 'Status for' : 'I did'} ${chore.title}${isDoneToday ? ': completed today' : ''}`}
                          disabled={
                            cannotComplete
                          }
                          onClick={() =>
                            completeChore(
                              chore
                            )
                          }
                        >
                          {isSubmitting
                            ? 'Saving...'

                            : isPendingToday
                            ? 'Waiting...'

                            : isApprovedToday
                            ? 'Done Today ✓'

                            : isRejectedToday
                            ? 'Done Today'

                            : 'I Did It!'}
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              }
            )}
            </ul>
          </Card>
        )}


        {/* ========================================
            Back
           ======================================== */}

        <Button
          variant="secondary"
          className="w-full"
          onClick={() =>
            navigate(
              '/kid/dashboard'
            )
          }
        >
          Back to Dashboard
        </Button>

      </div>
    </KidLayout>
  );
};

export default KidChores;

